import { apiClient } from './api';

export type VulnerabilitySeverity = 'critical' | 'high' | 'medium' | 'low';
export type VulnerabilityStatus = 'open' | 'acknowledged' | 'resolved' | 'false_positive';

/**
 * Which CVSS version cvss_score was taken from. The backend reports the newest
 * version a CVE was actually scored under, falling back 4.0 -> 3.1 -> 3.0 -> 2.0,
 * so the same list can mix versions and older CVEs still show a score.
 */
export type CvssVersion = '4.0' | '3.1' | '3.0' | '2.0';

export interface VulnerabilityListItem {
  id: number;
  organization_id: string;
  cve_id: string;
  severity: VulnerabilitySeverity;
  cvss_score: number | null;
  cvss_version: CvssVersion | null;
  status: VulnerabilityStatus;
  cpe_matched: string | null;
  detected_date: string;
  resolved_date: string | null;
  description: string;
  published_date: string;
  affected_software: string;
  software_id: number;
  software_name: string;
  software_version: string;
  asset_id: string | null;
  asset_name: string | null;
  asset_hostname: string | null;
}

export interface VulnerabilityDetail {
  id: number;
  organization_id: string;
  cve_id: string;
  severity: VulnerabilitySeverity;
  cvss_score: number | null;
  cvss_vector: string | null;
  cvss_version: CvssVersion | null;
  cvss40_score: number | null;
  cvss40_vector: string | null;
  cvss31_score: number | null;
  cvss31_vector: string | null;
  cvss30_score: number | null;
  cvss30_vector: string | null;
  cvss2_score: number | null;
  cvss2_vector: string | null;
  eps_score: number | null;
  is_known_exploited: boolean;
  status: VulnerabilityStatus;
  cpe_matched: string | null;
  detected_date: string;
  resolved_date: string | null;
  description: string;
  published_date: string;
  last_modified: string;
  references: Array<{ url: string; source: string | null; tags: string[] }>;
  weaknesses: string[];
  software: {
    id: number;
    name: string;
    version: string;
    publisher: string | null;
    source: string;
  };
  asset: {
    id: string;
    name: string;
    hostname: string | null;
    ip_address: string | null;
    os_name: string | null;
  };
}

export interface VulnerabilityListResponse {
  data: VulnerabilityListItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface VulnerabilityStatsResponse {
  total: number;
  recentNew: number;
  bySeverity: Record<VulnerabilitySeverity, number>;
  byStatus: Record<VulnerabilityStatus, number>;
}

export interface VulnerabilityTrendPoint {
  date: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
  assets: number;
}

export interface VulnerabilityScanLog {
  id: number;
  trigger: string;
  status: string;
  organizationId: string | null;
  assetsScanned: number;
  softwareScanned: number;
  vulnerabilitiesFound: number;
  newVulnerabilities: number;
  durationMs: number | null;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

export interface VulnerabilityScanLogsResponse {
  data: VulnerabilityScanLog[];
  page: number;
  pageSize: number;
  total: number;
}

export interface VulnerabilityQuery {
  severity?: VulnerabilitySeverity;
  status?: VulnerabilityStatus;
  search?: string;
  /** Exact CVE id, e.g. "CVE-2020-1171". Narrower than `search`. */
  cve?: string;
  assetId?: string;
  page?: number;
  pageSize?: number;
}

export interface SyncFeedsPayload {
  forceFullSync?: boolean;
}

export const vulnerabilityService = {
  async list(params: VulnerabilityQuery = {}): Promise<VulnerabilityListResponse> {
    return apiClient.get<VulnerabilityListResponse>('/asset/vulnerabilities', { params });
  },

  async get(id: number): Promise<VulnerabilityDetail> {
    return apiClient.get<VulnerabilityDetail>(`/asset/vulnerabilities/${id}`);
  },

  async getStats(
    params: { dateRange?: string; date_from?: string; date_to?: string } = {},
  ): Promise<VulnerabilityStatsResponse> {
    return apiClient.get<VulnerabilityStatsResponse>('/asset/vulnerabilities/stats', {
      params,
    });
  },

  async getTrend(params: { days?: number; date_from?: string; date_to?: string } = { days: 30 }): Promise<VulnerabilityTrendPoint[]> {
    return apiClient.get<VulnerabilityTrendPoint[]>('/asset/vulnerabilities/trend', {
      params,
    });
  },

  async getScanLogs(page = 1, pageSize = 20): Promise<VulnerabilityScanLogsResponse> {
    return apiClient.get<VulnerabilityScanLogsResponse>('/asset/vulnerabilities/scan-logs', {
      params: { page, pageSize },
    });
  },

  async triggerScan(payload: { assetId?: string } = {}): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/asset/vulnerabilities/scan', payload);
  },

  async syncFeeds(payload: SyncFeedsPayload = {}): Promise<{ message: string; jobId: string }> {
    return apiClient.post<{ message: string; jobId: string }>('/asset/vulnerabilities/sync-feeds', payload);
  },

  async syncFeedsAlias(payload: SyncFeedsPayload = {}): Promise<{ message: string; jobId: string }> {
    return apiClient.post<{ message: string; jobId: string }>('/asset/vulnerabilities/feed-sync', payload);
  },

  async getHealth(): Promise<any> {
    return apiClient.get<any>('/asset/vulnerabilities/health');
  },

  async updateStatus(
    id: number,
    status: VulnerabilityStatus,
  ): Promise<{ id: number; status: VulnerabilityStatus }> {
    return apiClient.patch<{ id: number; status: VulnerabilityStatus }>(
      `/asset/vulnerabilities/${id}/status`,
      { status },
    );
  },
};
